import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type MergeTicket } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };
const LABEL: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88" };

function TicketLine({ t }: { t: MergeTicket }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 7, minWidth: 0 }}>
      <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#006747", flex: "none" }}>{t.ref}</span>
      <span style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</span>
      <span style={{ fontSize: 11, color: "#8a8e88", flex: "none" }}>· {t.client}</span>
    </span>
  );
}

export function Duplicates() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  // Store the SELECTED tickets as full objects, not just ids, so a later search
  // that filters them out of the candidate list can't leave the form submitting
  // a ticket the user can no longer see.
  const [primarySel, setPrimarySel] = useState<MergeTicket | null>(null);
  const [duplicateSel, setDuplicateSel] = useState<MergeTicket | null>(null);
  // A merge/reverse can fail two ways: a thrown non-2xx (caught by isError) OR a
  // 200 carrying {ok:false}. Surface both — silence on the reversible action users
  // trust most reads as a no-op.
  const [mergeMsg, setMergeMsg] = useState<string | null>(null);
  const [reverseMsg, setReverseMsg] = useState<string | null>(null);

  // Debounce the search so we don't fire a full ticket scan on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(timer);
  }, [q]);

  const overview = useQuery({ queryKey: ["merge"], queryFn: api.mergeOverview });
  const candidates = useQuery({ queryKey: ["merge-candidates", debouncedQ], queryFn: () => api.mergeCandidates(debouncedQ) });

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["merge"] }),
      qc.invalidateQueries({ queryKey: ["merge-candidates"] }),
      qc.invalidateQueries({ queryKey: ["tickets"] }),
    ]);

  const doMerge = useMutation({
    mutationFn: (v: { primaryTicketId: string; duplicateTicketId: string; suggestionId?: string }) =>
      api.merge(v.primaryTicketId, v.duplicateTicketId, v.suggestionId),
    onMutate: () => setMergeMsg(null),
    onSuccess: (r) => {
      if (!r.ok) {
        setMergeMsg("Couldn't merge — the tickets may no longer be eligible. Refresh and try again.");
        return;
      }
      setPrimarySel(null);
      setDuplicateSel(null);
      invalidate();
    },
    onError: (e) => setMergeMsg(`Couldn't merge — ${e instanceof Error ? e.message : String(e)}`),
  });
  const reverse = useMutation({
    mutationFn: (id: string) => api.reverseMerge(id),
    onMutate: () => setReverseMsg(null),
    onSuccess: (r) => (r.ok ? invalidate() : setReverseMsg("Couldn't reverse this merge — it may be outside the 30-day window or already reversed.")),
    onError: (e) => setReverseMsg(`Couldn't reverse — ${e instanceof Error ? e.message : String(e)}`),
  });

  if (overview.isLoading) return <Loading />;
  if (overview.error) return <ErrorNote error={overview.error} />;
  const data = overview.data!;
  const items = candidates.data?.items ?? [];
  // A selected ticket stays in its own <select>'s options even when the current
  // search doesn't include it, so the form always shows what will be submitted.
  const optionsFor = (sel: MergeTicket | null) =>
    sel && !items.some((i) => i.id === sel.id) ? [sel, ...items] : items;
  const pick = (opts: MergeTicket[], id: string) => opts.find((t) => t.id === id) ?? null;
  const canMerge = !!primarySel && !!duplicateSel && primarySel.id !== duplicateSel.id && !doMerge.isPending;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Duplicates</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Merge a duplicate ticket into a primary. Merges are recorded here and reversible for 30 days.</div>
      </div>

      <div style={{ ...CARD, background: data.dedupeActive ? "#eef4f1" : "#fbf6e9", boxShadow: `inset 0 0 0 1px ${data.dedupeActive ? "#cfe0d6" : "#efe2c0"}`, fontSize: 12.5, color: "#4a504a", display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name={data.dedupeActive ? "check-circle" : "circle"} size={14} color={data.dedupeActive ? "#006747" : "#9a7b1f"} />
        {data.dedupeActive
          ? "Inbox de-duplication is ON — merged duplicates are hidden from the queue."
          : "Merges are recorded but merged duplicates still appear in the inbox. Enable the merge_dedupe flag to hide them."}
      </div>

      {mergeMsg && (
        <div style={{ ...CARD, background: "#fbe9e7", boxShadow: "inset 0 0 0 1px #f3d3ce", color: "#b3261e", fontSize: 12.5 }}>
          {mergeMsg}
        </div>
      )}

      {/* Build a merge */}
      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Merge two tickets</div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search open tickets by ref or subject…"
          style={{ width: "100%", boxSizing: "border-box", fontSize: 13, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 7, marginBottom: 10 }}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ ...LABEL, marginBottom: 5 }}>Primary (kept)</div>
            <select value={primarySel?.id ?? ""} onChange={(e) => setPrimarySel(pick(optionsFor(primarySel), e.target.value))} style={{ width: "100%", fontSize: 12.5, padding: "7px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }}>
              <option value="">Choose the ticket to keep…</option>
              {optionsFor(primarySel).map((t) => <option key={t.id} value={t.id} disabled={t.id === duplicateSel?.id}>{t.ref} — {t.subject}</option>)}
            </select>
          </div>
          <div>
            <div style={{ ...LABEL, marginBottom: 5 }}>Duplicate (merged away)</div>
            <select value={duplicateSel?.id ?? ""} onChange={(e) => setDuplicateSel(pick(optionsFor(duplicateSel), e.target.value))} style={{ width: "100%", fontSize: 12.5, padding: "7px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }}>
              <option value="">Choose the duplicate…</option>
              {optionsFor(duplicateSel).map((t) => <option key={t.id} value={t.id} disabled={t.id === primarySel?.id}>{t.ref} — {t.subject}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
          <button
            onClick={() => canMerge && doMerge.mutate({ primaryTicketId: primarySel!.id, duplicateTicketId: duplicateSel!.id })}
            disabled={!canMerge}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#fff", background: canMerge ? "#006747" : "#b7c3bd", border: 0, borderRadius: 7, padding: "8px 13px", cursor: canMerge ? "pointer" : "default" }}
          >
            <Icon name="git-merge" size={14} color="#fff" />{doMerge.isPending ? "Merging…" : "Merge duplicate"}
          </button>
          {candidates.isFetching && <span style={{ fontSize: 11.5, color: "#8a8e88" }}>Searching…</span>}
          {!candidates.isFetching && candidates.isError && <span style={{ fontSize: 11.5, color: "#b3261e" }}>Couldn't search tickets — try again.</span>}
          {!candidates.isFetching && !candidates.isError && items.length === 0 && <span style={{ fontSize: 11.5, color: "#8a8e88" }}>No open tickets match.</span>}
        </div>
      </div>

      {/* AI-suggested duplicates */}
      {data.suggestions.length > 0 && (
        <div style={CARD}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Suggested duplicates</div>
          {data.suggestions.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "1px solid #f4f3ee" }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                <TicketLine t={s.duplicate} />
                <span style={{ fontSize: 11, color: "#8a8e88" }}>→ into <span style={{ fontFamily: "'Space Mono',monospace", color: "#006747" }}>{s.primary.ref}</span>{s.confidence != null ? ` · ${s.confidence}% match` : ""}</span>
              </div>
              {(() => {
                // Serialize on the shared mutation (disable ALL while any merge is in
                // flight) so a second merge can't start and race/duplicate; use
                // `variables` (latest call) only for the active row's label/dim.
                const rowMerging = doMerge.isPending && doMerge.variables?.suggestionId === s.id;
                return (
                  <button
                    onClick={() => doMerge.mutate({ primaryTicketId: s.primary.id, duplicateTicketId: s.duplicate.id, suggestionId: s.id })}
                    disabled={doMerge.isPending}
                    style={{ flex: "none", fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #cfe0d6", borderRadius: 7, padding: "6px 11px", cursor: doMerge.isPending ? "default" : "pointer", opacity: doMerge.isPending && !rowMerging ? 0.5 : rowMerging ? 0.6 : 1 }}
                  >
                    {rowMerging ? "Merging…" : "Merge"}
                  </button>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Recent merges */}
      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Recent merges</div>
        {reverseMsg && (
          <div style={{ background: "#fbe9e7", border: "1px solid #f3d3ce", color: "#b3261e", fontSize: 12, borderRadius: 6, padding: "7px 11px", marginBottom: 8 }}>{reverseMsg}</div>
        )}
        {data.recent.length === 0 ? (
          <div style={{ color: "#8a8e88", fontSize: 13 }}>No merges yet.</div>
        ) : (
          data.recent.map((m) => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "1px solid #f4f3ee" }}>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: 12.5, display: "inline-flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
                  <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: m.reversedAt ? "#8a8e88" : "#006747" }}>{m.merged.ref}</span>
                  <span style={{ color: "#8a8e88", flex: "none" }}>merged into</span>
                  <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#006747" }}>{m.primary.ref}</span>
                </span>
                <span style={{ fontSize: 11, color: "#8a8e88" }}>
                  {m.actor ?? "—"}{m.reversedAt ? " · reversed" : m.reversible ? " · reversible" : " · reverse window closed"}
                </span>
              </div>
              {!m.reversedAt && m.reversible && (() => {
                // Same serialization: all reverse buttons disable while any reverse is
                // in flight; `variables` drives only the active row's label/dim.
                const rowReversing = reverse.isPending && reverse.variables === m.id;
                return (
                  <button
                    onClick={() => reverse.mutate(m.id)}
                    disabled={reverse.isPending}
                    style={{ flex: "none", fontSize: 12, fontWeight: 600, color: "#8a5a00", background: "#fff", border: "1px solid #efe2c0", borderRadius: 7, padding: "6px 11px", cursor: reverse.isPending ? "default" : "pointer", opacity: reverse.isPending && !rowReversing ? 0.5 : rowReversing ? 0.6 : 1 }}
                  >
                    {rowReversing ? "Reversing…" : "Reverse"}
                  </button>
                );
              })()}
            </div>
          ))
        )}
      </div>
    </Page>
  );
}
